import React from "react";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CHART_PLANS, suggestedChartPlan } from "@/lib/householdConstants";
import { cn } from "@/lib/utils";
import { BookOpenText, Lock } from "lucide-react";

/**
 * Valg av kontoplan for en virksomhet (husholdning / NS 4102 AS / NS 4102 ENK).
 * Husholdningsregnskap er sperret når klienten har org.nr (registrert i Brønnøysundregistrene).
 * Planen som passer type/org.nr merkes «anbefalt» – den velges aldri automatisk for eksisterende virksomheter.
 */
export const ChartPlanPicker = ({ value, onChange, orgNumber, entityType, idPrefix = "chart-plan", showLabel = true, missingHint = false, testId = "workspace-chart-plan" }) => {
  const hasOrg = (orgNumber || "").replace(/\D/g, "").length > 0;
  const suggested = suggestedChartPlan(entityType, orgNumber);
  return (
    <div className="space-y-2">
      {showLabel ? <Label className="flex items-center gap-1.5"><BookOpenText className="h-4 w-4 text-primary" /> Kontoplan for regnskapet</Label> : null}
      <RadioGroup value={value || ""} onValueChange={onChange} className="grid gap-2" data-testid={testId}>
        {Object.entries(CHART_PLANS).map(([key, p]) => {
          const disabled = key === "husholdning" && hasOrg;
          const active = value === key;
          return (
            <label key={key} htmlFor={`${idPrefix}-${key}`} className={cn("flex items-start gap-3 rounded-lg border p-3 transition-colors", disabled ? "opacity-60 cursor-not-allowed bg-secondary/40" : "cursor-pointer", active && !disabled ? "border-primary/40 bg-accent/30" : !disabled && "hover:bg-secondary/60")} data-testid={`${testId}-option-${key}`}>
              <RadioGroupItem value={key} id={`${idPrefix}-${key}`} disabled={disabled} className="mt-0.5" data-testid={`${testId}-${key}`} />
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <p.icon className={cn("h-4 w-4 shrink-0", active && !disabled ? "text-primary" : "text-muted-foreground")} />
                  <span className="text-sm font-medium">{p.label}</span>
                  {suggested === key && !disabled ? <Badge variant="outline" className="rounded-full text-[10px] font-normal">anbefalt</Badge> : null}
                  {disabled ? <Badge variant="outline" className="rounded-full text-[10px] font-normal inline-flex items-center gap-1"><Lock className="h-3 w-3" /> krever at klienten ikke har org.nr</Badge> : null}
                </span>
                <span className="block text-xs text-muted-foreground mt-0.5">{p.description}</span>
              </span>
            </label>
          );
        })}
      </RadioGroup>
      <p className="text-xs text-muted-foreground" data-testid={`${testId}-hint`}>
        {missingHint ? "Virksomheten har ingen kontoplan ennå – Regnskap-modulen venter til du har valgt. " : ""}
        {hasOrg ? "Klienten er registrert i Brønnøysundregistrene (org.nr) og bruker Norsk Standard kontoplan NS 4102." : "Uten org.nr kan klienten føre husholdningsregnskap. Legges org.nr til senere, byttes et husholdningsvalg automatisk til NS 4102."}
      </p>
    </div>
  );
};
